/** XeAgents.ai **/
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  Users,
  UserPlus,
  Mail,
  Trash2,
  ShieldCheck,
  Search,
  MoreVertical,
  Crown,
  Loader2,
  Coins,
  AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";
import { formatDate } from "@/lib/date-format";

interface Member {
  id: string;
  userId: string;
  role: string;
  joinedAt: string;
  user: {
    name: string;
    email: string;
    mustChangePassword?: boolean;
  };
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  inviter: {
    id: string;
    name: string;
    email: string;
  };
}

type TeamMemberItem = 
  | { type: 'member'; data: Member }
  | { type: 'invitation'; data: Invitation };

interface User {
  id: string;
  role?: string;
  credits?: number;
  planType?: string;
}

export default function TeamMembers() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const [searchQuery, setSearchQuery] = useState("");
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"owner" | "member">("member");
  const [inviteCredits, setInviteCredits] = useState<number>(0);
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [roleChangeDialog, setRoleChangeDialog] = useState<{ member: Member; newRole: string } | null>(null);

  // Fetch current user to check permissions
  const { data: currentUser } = useQuery<User>({
    queryKey: ["/api/auth/me"],
  });

  // Fetch members
  const { data: members, isLoading: membersLoading } = useQuery<Member[]>({
    queryKey: ["/api/organizations/members"],
    select: (data: any[]) =>
      data.map(({ member, user }) => ({
        id: member.id,
        userId: member.userId,
        role: member.role,
        joinedAt: member.joinedAt,
        user: { name: user.name, email: user.email, mustChangePassword: user.mustChangePassword },
      })),
  });

  // Fetch pending invitations
  const { data: invitations, isLoading: invitationsLoading } = useQuery<Invitation[]>({
    queryKey: ["/api/organizations/invitations"],
    select: (data: any[]) =>
      data.map(({ invitation, inviter }) => ({
        id: invitation.id,
        email: invitation.email,
        role: invitation.role,
        status: invitation.status,
        createdAt: invitation.createdAt,
        expiresAt: invitation.expiresAt,
        inviter: { id: inviter.id, name: inviter.name, email: inviter.email },
      })),
  });

  const isLoading = membersLoading || invitationsLoading;

  // Combine members and invitations into a unified list
  const teamMembers: TeamMemberItem[] = [
    ...(members?.map(m => ({ type: 'member' as const, data: m })) || []),
    ...(invitations?.map(i => ({ type: 'invitation' as const, data: i })) || []),
  ];

  // Invite member mutation
  const inviteMutation = useMutation({
    mutationFn: async (data: { email: string; role: string; credits?: number }) => {
      const response = await apiRequest("POST", "/api/organizations/invitations", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/invitations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({
        title: "Invitation sent",
        description: inviteCredits > 0 
          ? `Invitation sent with ${inviteCredits} credits allocated`
          : "The invitation has been sent successfully",
      });
      setInviteDialogOpen(false);
      setInviteEmail("");
      setInviteRole("member");
      setInviteCredits(0);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Remove member mutation
  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiRequest("DELETE", `/api/organizations/members/${userId}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/members"] });
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/seats"] });
      toast({
        title: "Member removed",
        description: "The member has been removed from the organization",
      });
      setMemberToRemove(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to remove member",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async (data: { userId: string; role: string }) => {
      const response = await apiRequest("PATCH", `/api/organizations/members/${data.userId}/role`, { role: data.role });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/members"] });
      toast({
        title: "Role updated",
        description: "The member's role has been updated successfully",
      });
      setRoleChangeDialog(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update role",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Filter team members based on search query
  const filteredMembers = teamMembers.filter((item) => {
    if (item.type === 'member') {
      return item.data.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
             item.data.user.email.toLowerCase().includes(searchQuery.toLowerCase());
    } else {
      return item.data.email.toLowerCase().includes(searchQuery.toLowerCase());
    }
  });

  const handleInvite = () => {
    // Check if user is on free plan
    if (currentUser?.planType === "free") {
      toast({
        title: "Upgrade Required",
        description: "Inviting members is not available on the free plan. Please upgrade to a paid plan to add team members.",
        variant: "destructive",
      });
      return;
    }

    if (!inviteEmail) {
      toast({
        title: "Email required",
        description: "Please enter an email address",
        variant: "destructive",
      });
      return;
    }

    // Credits are required
    if (!inviteCredits || inviteCredits <= 0) {
      toast({
        title: "Credits required",
        description: "Please allocate credits to the new member before sending the invitation.",
        variant: "destructive",
      });
      return;
    }

    // Validate credits don't exceed available balance
    const availableCredits = currentUser?.credits || 0;
    if (inviteCredits > availableCredits) {
      toast({
        title: "Insufficient credits",
        description: `You only have ${availableCredits} credits available. Cannot allocate ${inviteCredits} credits.`,
        variant: "destructive",
      });
      return;
    }

    inviteMutation.mutate({ 
      email: inviteEmail, 
      role: inviteRole,
      credits: inviteCredits > 0 ? inviteCredits : undefined
    });
  };

  const handleRemoveMember = (member: Member) => {
    setMemberToRemove(member);
  };

  const confirmRemove = () => {
    if (memberToRemove) {
      removeMutation.mutate(memberToRemove.userId);
    }
  };

  // Cancel invitation mutation
  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await apiRequest("DELETE", `/api/organizations/invitations/${invitationId}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/invitations"] });
      toast({
        title: "Invitation cancelled",
        description: "The invitation has been cancelled successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to cancel invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Resend invitation mutation
  const resendInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const response = await apiRequest("POST", `/api/organizations/invitations/${invitationId}/resend`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/organizations/invitations"] });
      toast({
        title: "Invitation resent",
        description: "The invitation has been resent successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to resend invitation",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Resend temporary password mutation
  const resendPasswordMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiRequest("POST", `/api/organizations/members/${userId}/resend-password`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Password resent",
        description: "The temporary password has been sent to the member's email",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to resend password",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleRoleChange = (member: Member, newRole: string) => {
    setRoleChangeDialog({ member, newRole });
  };

  const confirmRoleChange = () => {
    if (roleChangeDialog) {
      updateRoleMutation.mutate({
        userId: roleChangeDialog.member.userId,
        role: roleChangeDialog.newRole,
      });
    }
  };

  // Check if current user is owner
  const isOwner = currentUser?.role === "owner" || members?.find(m => m.userId === currentUser?.id)?.role === "owner";
  const isFreePlan = currentUser?.planType === "free";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Team Members</h1>
          <p className="text-muted-foreground mt-1">
            Manage your organization's team members and permissions
          </p>
        </div>
        {isOwner && (
          <Button 
            onClick={() => setInviteDialogOpen(true)}
            disabled={isFreePlan}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Member
          </Button>
        )}
      </div>

      {/* Free Plan Warning */}
      {isOwner && isFreePlan && (
        <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 dark:text-amber-100">
                  Free Plan Limitation
                </h3>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  Only one user is allowed on the free plan. To invite team members and collaborate with your organization, please upgrade to a paid plan.
                </p>
                <Button 
                  size="sm" 
                  className="mt-3"
                  onClick={() => setLocation('/app/upgrade')}
                >
                  Upgrade Now
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Members List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            All Members ({filteredMembers.length})
          </CardTitle>
          <CardDescription>
            Current members of your organization
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <Skeleton className="h-12 w-12 rounded-full" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">No members found</p>
              <p className="text-sm mt-1">
                {searchQuery ? "Try a different search term" : "Invite your first team member"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMembers.map((item) => {
                if (item.type === 'member') {
                  const member = item.data;
                  const isCurrentUser = member.userId === currentUser?.id;
                  const isMemberOwner = member.role === "owner";

                  return (
                    <div
                      key={`member-${member.id}`}
                      className="flex items-center gap-4 p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      {/* Avatar */}
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-lg font-medium text-primary">
                          {member.user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>

                      {/* Member Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">
                            {member.user.name}
                            {isCurrentUser && (
                              <span className="text-muted-foreground ml-1">(You)</span>
                            )}
                          </p>
                          {isMemberOwner && <Crown className="h-4 w-4 text-amber-500" />}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          {member.user.email}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {member?.joinedAt ? `Joined ${formatDate(member.joinedAt, "MMM d, yyyy")}` : "—"}
                        </p>
                      </div>

                      {/* Role Badge */}
                      <Badge variant={isMemberOwner ? "default" : "secondary"} className="flex-shrink-0">
                        {member.role}
                      </Badge>

                      {/* Actions (only for owners, and not for themselves) */}
                      {isOwner && !isCurrentUser && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!isMemberOwner && (
                              <DropdownMenuItem onClick={() => handleRoleChange(member, "owner")}>
                                <ShieldCheck className="h-4 w-4 mr-2" />
                                Make Owner
                              </DropdownMenuItem>
                            )}
                            {isMemberOwner && (
                              <DropdownMenuItem onClick={() => handleRoleChange(member, "member")}>
                                <Users className="h-4 w-4 mr-2" />
                                Make Member
                              </DropdownMenuItem>
                            )}
                            {member.user.mustChangePassword && (
                              <DropdownMenuItem onClick={() => resendPasswordMutation.mutate(member.userId)}>
                                <Mail className="h-4 w-4 mr-2" />
                                Resend Password
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => handleRemoveMember(member)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                } else {
                  // Pending invitation
                  const invitation = item.data;

                  return (
                    <div
                      key={`invitation-${invitation.id}`}
                      className="flex items-center gap-4 p-4 rounded-lg border border-dashed hover:bg-muted/50 transition-colors"
                    >
                      {/* Avatar */}
                      <div className="h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                        <Mail className="h-6 w-6 text-amber-600 dark:text-amber-500" />
                      </div>

                      {/* Invitation Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">
                            {invitation.email}
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">
                          Invited by {invitation.inviter.name}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Expires {formatDate(invitation.expiresAt, "MMM d, yyyy")}
                        </p>
                      </div>

                      {/* Status Badge */}
                      <Badge variant="outline" className="flex-shrink-0 border-amber-500 text-amber-600 dark:text-amber-500">
                        Pending
                      </Badge>

                      {/* Role Badge */}
                      <Badge variant="secondary" className="flex-shrink-0">
                        {invitation.role}
                      </Badge>

                      {/* Actions (only for owners) */}
                      {isOwner && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => resendInvitationMutation.mutate(invitation.id)}>
                              <Mail className="h-4 w-4 mr-2" />
                              Resend Invitation
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => cancelInvitationMutation.mutate(invitation.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Cancel Invitation
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                }
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation to join your organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select value={inviteRole} onValueChange={(value: "owner" | "member") => setInviteRole(value)}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {inviteRole === "owner"
                  ? "Owners can manage members and organization settings"
                  : "Members can use organization resources"}
              </p>
            </div>

            {/* Credit Allocation Section */}
            <div className="space-y-2">
              <Label htmlFor="credits" className="flex items-center gap-2">
                <Coins className="h-4 w-4" />
                Allocate Credits <span className="text-destructive">*</span>
              </Label>
              <Input
                id="credits"
                type="number"
                min="1"
                max={currentUser?.credits || 0}
                placeholder="0"
                value={inviteCredits || ""}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10) || 0;
                  setInviteCredits(value);
                }}
              />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Available: {currentUser?.credits?.toLocaleString() || 0} credits</span>
                {inviteCredits > 0 && (
                  <span className="text-primary font-medium">
                    Allocating: {inviteCredits.toLocaleString()}
                  </span>
                )}
              </div>
              {inviteCredits > (currentUser?.credits || 0) && (
                <div className="flex items-center gap-1 text-xs text-destructive">
                  <AlertTriangle className="h-3 w-3" />
                  <span>Exceeds available credits</span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleInvite} disabled={inviteMutation.isPending}>
              {inviteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{memberToRemove?.user.name}</strong> from the organization?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Role Change Confirmation Dialog */}
      <AlertDialog open={!!roleChangeDialog} onOpenChange={() => setRoleChangeDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Member Role</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to change <strong>{roleChangeDialog?.member.user.name}</strong>'s role to{" "}
              <strong>{roleChangeDialog?.newRole}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRoleChange}>
              {updateRoleMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Change Role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}